import { cloneDeep } from 'lodash';
import type { RootStore } from '~/stores/root.store';
import {
    IAutomationTemplate,
    IAutomationInstance,
    TAutomationStepExecuteRequest
} from '../types/automation.types';
import {
    AutomationsAdapter,
    UpdateAutomationRequest
} from '~/services/adapters/automations.adapter';
import {
    TWorkflowAutomationTimeFrame,
    WorkflowAutomationType,
    WorkflowAutomationTypeValues
} from '~/types/enums.types';
import { withBase64Encoding } from '~/utils/text.utils';

export type UpdateWorkflowAutomationInput = {
    companyId: string;
    automationType: WorkflowAutomationType;
    isOn?: boolean;
    timeFrame?: TWorkflowAutomationTimeFrame;
    questionnaireId?: string;
    flowTemplateId?: string;
    emailTemplateId?: string;
    reminderMessage?: {
        subject: string;
        htmlBody: string;
    };
};

export class AutomationService extends AutomationsAdapter {
    rootStore: RootStore;

    constructor(rootStore: RootStore) {
        super(rootStore.apiService);
        this.rootStore = rootStore;
    }

    async createTemplatesFromCustomEmails(
        companyId: string,
        automationId: string
    ) {
        const { apiService } = this.rootStore;
        const response = await apiService.apiRequest<{
            new_templates: string[];
        }>({
            method: 'post',
            url: `/api/v2/company/${companyId}/workflows/${automationId}/create_templates_from_custom_emails`
        });

        return response.new_templates;
    }

    async fetchAutomationTemplate(companyId: string, automationId: string) {
        const { apiService } = this.rootStore;
        const response = await apiService.apiRequest<IAutomationTemplate>({
            method: 'get',
            url: `/api/v2/company/${companyId}/workflows/${automationId}`
        });

        return response;
    }

    async updateAutomationTemplate(data: IAutomationTemplate) {
        const { apiService } = this.rootStore;
        const { currCompanyId } = this.rootStore.sessionStore;
        const automation = this.encodeHtmlOnAutomation(data);

        const response = await apiService.apiRequest<IAutomationTemplate>({
            method: 'put',
            url: `/api/v2/company/${currCompanyId}/workflows/${data._id}`,
            data: automation
        });
        return response;
    }

    async deleteAutomationTemplate(automationId: string) {
        const { apiService } = this.rootStore;
        const { currCompanyId } = this.rootStore.sessionStore;

        const response = await apiService.apiRequest<IAutomationTemplate>({
            method: 'delete',
            url: `/api/v2/company/${currCompanyId}/workflows/${automationId}`
        });

        return response;
    }

    async getTempAutomationInstance(workspaceId: string, automationId: string) {
        const { apiService } = this.rootStore;
        const response = await apiService.apiRequest<IAutomationInstance>({
            method: 'get',
            url: `/api/v2/workspaces/${workspaceId}/workflows/${automationId}/instance`
        });

        return response;
    }

    async applyAutomation(
        workspaceId: string,
        automation: IAutomationInstance
    ) {
        const { apiService } = this.rootStore;
        const newAutomation = this.encodeHtmlOnAutomation(automation);
        const response = await apiService.apiRequest<IAutomationInstance>({
            method: 'post',
            url: `/api/v2/workspaces/${workspaceId}/workflows/${automation._id}/set_instance`,
            data: { workflow_instance: newAutomation }
        });

        return response;
    }

    async getFlowsWithContract(companyId: string, automationId: string) {
        const { apiService } = this.rootStore;
        const response = await apiService.apiRequest<string[]>({
            method: 'get',
            url: `/api/v2/company/${companyId}/workflows/${automationId}/flows_with_contract`
        });
        return response;
    }

    async updateAutomationInstance(
        workspaceId: string,
        automation: IAutomationInstance
    ) {
        const { apiService } = this.rootStore;
        const newAutomation = this.encodeHtmlOnAutomation(automation);
        const response = await apiService.apiRequest<IAutomationInstance>({
            method: 'post',
            url: `/api/v2/workspaces/${workspaceId}/workflows/${automation._id}/update`,
            data: {
                title: automation.title,
                workflow_steps: newAutomation.workflow_steps
            }
        });

        return response;
    }

    async executeAutomationStep(
        workspaceId: string,
        stepId: string,
        data: TAutomationStepExecuteRequest
    ) {
        const { apiService } = this.rootStore;
        const payload: TAutomationStepExecuteRequest & {
            body?: string;
            encoded?: boolean;
        } = {
            ...data
        };
        if (payload.body) {
            // If the data contains a custom email body, encode it to base64
            // so we will not mess with cloudflare's security policies
            payload.body = withBase64Encoding(payload.body);
            payload.encoded = true;
        }
        const { workflow: automation } = await apiService.apiRequest<{
            _id: string; // workspaceId
            workflow: IAutomationInstance;
        }>({
            method: 'put',
            url: `/api/v2/workspaces/${workspaceId}/workflow_step/${stepId}`,
            data: payload
        });

        return automation;
    }

    async exportIncompleteOldAutomationsByAccount(companyId: string) {
        return this.apiService.apiRequest<{ url: string }>({
            method: 'get',
            url: `/api/v2/company/${companyId}/workflows/incomplete_workflows_report_by_account`
        });
    }

    private encodeHtmlOnAutomation(automation) {
        const newAutomation = cloneDeep(automation);
        newAutomation?.workflow_steps.forEach(step => {
            const html_body =
                step?.workflow_step_action?.generic_email?.html_body;
            if (html_body) {
                step.workflow_step_action.generic_email.html_body =
                    this.toBinaryBase64(html_body);
            }
        });

        return newAutomation;
    }

    // convert a Unicode string to a string in which
    // each 16-bit unit occupies only one byte
    private toBinaryBase64(string) {
        const codeUnits = new Uint16Array(string.length);
        for (let i = 0; i < codeUnits.length; i++) {
            codeUnits[i] = string.charCodeAt(i);
        }
        return btoa(
            new Uint8Array(codeUnits.buffer).reduce((data, byte) => {
                return data + String.fromCharCode(byte);
            }, '')
        );
    }

    async updateWorkflowAutomation({
        companyId,
        automationType,
        isOn,
        timeFrame,
        questionnaireId,
        flowTemplateId,
        emailTemplateId,
        reminderMessage
    }: UpdateWorkflowAutomationInput) {
        const ordinal = WorkflowAutomationTypeValues.indexOf(automationType);
        const automation: UpdateAutomationRequest = {
            automation_type_cd: ordinal,
            ...(timeFrame === undefined
                ? {}
                : { relevant_time_frame: timeFrame }),
            ...(isOn === undefined ? {} : { is_automation_on: isOn }),
            ...(flowTemplateId === undefined
                ? {}
                : { flow_template_id: flowTemplateId }),
            ...(questionnaireId === undefined
                ? {}
                : { questionnaire_template_id: questionnaireId }),
            ...(emailTemplateId === undefined
                ? {}
                : { email_template_id: emailTemplateId }),
            ...(reminderMessage === undefined
                ? {}
                : {
                      html_body: reminderMessage.htmlBody,
                      subject: reminderMessage.subject
                  })
        };
        await this.updateAutomation(companyId, automation);
        return automation;
    }
}
